import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, ExternalLink, FileText } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { Panel } from "@/components/common/Primitives";
import { UsageBars } from "@/components/UsageMeter";
import { api, apiError } from "@/lib/api";
import { dateTime, eur } from "@/lib/format";

const STATUS_LABELS = {
  TRIAL: "Proefperiode",
  ACTIVE: "Actief",
  PAYMENT_DUE: "Betaling openstaand",
  PAST_DUE: "Betaling achterstallig",
  CANCELLED: "Beëindigd",
  MANUAL: "Handmatige facturatie",
};

const STATUS_STYLES = {
  ACTIVE: "bg-emerald-50 text-emerald-700",
  TRIAL: "bg-blue-50 text-blue-700",
  PAYMENT_DUE: "bg-amber-50 text-amber-700",
  PAST_DUE: "bg-red-50 text-red-700",
  CANCELLED: "bg-slate-100 text-slate-600",
  MANUAL: "bg-slate-100 text-slate-600",
};

const Row = ({ k, v }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0">
    <span className="text-[12px] text-slate-500">{k}</span>
    <span className="mono text-[12px] text-slate-800">{v ?? "—"}</span>
  </div>
);

export default function Subscription() {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [overview, used] = await Promise.all([
        api.get("/billing/overview"),
        api.get("/usage"),
      ]);
      setData(overview.data);
      setUsage(used.data);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data: session } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = session.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Abonnement opzeggen per einde van de huidige periode?")) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel", {});
      toast.success("Abonnement wordt beëindigd aan het einde van de periode");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const subscription = data?.subscription || {};
  const pricing = data?.pricing || {};
  const billing = data?.billing || {};
  const invoices = data?.invoices || [];
  const status = subscription.status || "TRIAL";

  return (
    <AppShell
      title="Abonnement en gebruik"
      subtitle="Jouw ImDash-abonnement, maandgebruik en facturen"
    >
      {error && (
        <div className="panel mb-3 border-red-300 bg-red-50 p-3 text-[13px] text-red-800">
          {error}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title="Abonnement" subtitle="Prijs en status" testid="subscription-panel">
          <div className="p-3">
            <div className="flex items-center gap-2">
              <span
                className={`chip ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600"}`}
                data-testid="subscription-status"
              >
                {STATUS_LABELS[status] || status}
              </span>
              {billing.cancel_at_period_end && (
                <span className="chip bg-amber-50 text-amber-700">Opgezegd per einde periode</span>
              )}
            </div>
            <div className="mt-3">
              <Row k="Pakket" v={pricing.plan_name} />
              <Row
                k="Maandprijs"
                v={`${eur(pricing.effective_price, { fallback: "—" })} ${
                  pricing.currency || "EUR"
                } per maand`}
              />
              <Row k="Proefperiode tot" v={dateTime(subscription.trial_ends_at)} />
              <Row k="Laatste betaling" v={dateTime(billing.last_payment_at)} />
              <Row k="Volgende afschrijving" v={dateTime(billing.next_payment_due_at)} />
            </div>
            {!billing.stripe_configured && (
              <p className="mt-3 rounded-sm border border-amber-200 bg-amber-50 px-2.5 py-2 text-[12px] text-amber-800">
                Online betalen is nog niet geactiveerd. Neem contact op met ImDash voor facturatie.
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={startCheckout}
                disabled={busy || !billing.stripe_configured}
                className="btn-primary"
                data-testid="subscription-pay-button"
              >
                <CreditCard className="mr-1.5 inline h-3.5 w-3.5" />
                {billing.has_payment_method ? "Betaalgegevens vernieuwen" : "Abonnement activeren"}
              </button>
              {billing.subscription_id && !billing.cancel_at_period_end && (
                <button
                  type="button"
                  onClick={cancel}
                  disabled={busy}
                  className="btn-secondary text-red-700"
                  data-testid="subscription-cancel-button"
                >
                  Opzeggen per einde periode
                </button>
              )}
            </div>
          </div>
        </Panel>

        <Panel
          title="Gebruik deze maand"
          subtitle="Scans, AI-analyses en verversingen binnen je abonnement"
          testid="usage-panel"
        >
          <div className="p-3">
            <UsageBars usage={usage} testid="customer-usage-bars" />
          </div>
        </Panel>
      </div>

      <Panel
        className="mt-3"
        title="Facturen"
        subtitle="Alle facturen van je ImDash-abonnement"
        testid="invoices-panel"
      >
        {invoices.length ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Factuurnummer</th>
                <th>Bedrag</th>
                <th>Status</th>
                <th>Bekijken</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                  <td className="mono text-[11px]">{dateTime(invoice.created_at)}</td>
                  <td className="mono text-[11px]">{invoice.number || invoice.id}</td>
                  <td className="mono text-[12px]">{eur(invoice.amount_paid || invoice.amount_due)}</td>
                  <td className="text-[12px]">{invoice.status}</td>
                  <td className="flex gap-2 text-[12px]">
                    {invoice.hosted_invoice_url && (
                      <a
                        href={invoice.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" /> Online
                      </a>
                    )}
                    {invoice.invoice_pdf && (
                      <a
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                      >
                        <FileText className="h-3 w-3" /> PDF
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="p-3 text-[13px] text-slate-500" data-testid="invoices-empty">
            Nog geen facturen. Zodra het abonnement is geactiveerd verschijnen ze hier.
          </p>
        )}
      </Panel>
    </AppShell>
  );
}
